import React from "react";
import { useShallow } from "zustand/react/shallow";
import {
  Activity,
  BarChart3,
  Bug,
  Camera,
  GitBranch,
  Grid,
  Grid3X3,
  History,
  Info,
  Keyboard,
  Link2,
  Lightbulb,
  ListTree,
  Mail,
  MessageSquare,
  Mountain,
  Network,
  Package,
  Palette,
  Play,
  Radio,
  Ruler,
  Settings,
  ShieldCheck,
  SlidersHorizontal,
  Upload,
  Weight,
  Zap,
} from "lucide-react";
import { PanelErrorBoundary } from "../ErrorBoundaryFallbacks";
import DraggablePanel from "../draggable-panels/DraggablePanel";
import SceneGraphPanel from "../draggable-panels/SceneGraphPanel";
import PhysicsPanel from "../draggable-panels/PhysicsPanel";
import PhysicsRuntimePanel from "../draggable-panels/PhysicsRuntimePanel";
import ActuatorControlsPanel from "../draggable-panels/ActuatorControlsPanel";
import HistoryPanel from "../draggable-panels/HistoryPanel";
import AssetStatsPanel from "../draggable-panels/AssetStatsPanel";
import IkTargetPanel from "../draggable-panels/IkTargetPanel";
import BehaviorEditorPanel from "../draggable-panels/BehaviorEditorPanel";
import CameraProbePanel from "../draggable-panels/CameraProbePanel";
import SensorsPanel from "../draggable-panels/SensorsPanel";
import LightsPanel from "../draggable-panels/LightsPanel";
import OctoTrainingPanel from "../draggable-panels/OctoTrainingPanel";
import XmlSourceOfTruthPanel from "../draggable-panels/XmlSourceOfTruthPanel";
import TerrainPanel from "../draggable-panels/TerrainPanel";
import SceneInventoryPanel from "../draggable-panels/SceneInventoryPanel";
import ModelCheckPanel from "../draggable-panels/ModelCheckPanel";
import MassPropertiesPanel from "../draggable-panels/MassPropertiesPanel";
import MeasurementsPanel from "../draggable-panels/MeasurementsPanel";
import VisibilitySettingsPanel from "../draggable-panels/VisibilitySettingsPanel";
import ShortcutsPanel from "../draggable-panels/ShortcutsPanel";
import AboutPanel from "../draggable-panels/AboutPanel";
import { SceneBuilderIcon } from "../ui/Icons";
import { MOBILE_TOOL_PANEL_PIN_IDS } from "../../constants/mobileToolPanelPins";
import { usePanelStore } from "../../stores/panelStore";
import { XML_SOURCE_GROUPS } from "../../utils/libraryCatalog";
import { FileUploadPanelContainer, PoseEditorPanelContainer } from "./MainPanelOverlays";

const MemoSceneGraphPanel = React.memo(SceneGraphPanel);
const LazyGetUpdatesPanel = React.lazy(() => import("../draggable-panels/GetUpdatesPanel"));
const LazySendFeedbackPanel = React.lazy(() => import("../draggable-panels/SendFeedbackPanel"));
const LazyMaterialEditorPanel = React.lazy(() => import("../draggable-panels/MaterialEditorPanel"));
const LazyWarmstartDatasetPanel = React.lazy(
  () => import("../draggable-panels/WarmstartDatasetPanel")
);
const LazyTrainingPipelinePanel = React.lazy(
  () => import("../draggable-panels/TrainingPipelinePanel")
);
const LazyTrainingMetricsPanel = React.lazy(
  () => import("../draggable-panels/TrainingMetricsPanel")
);
const LazyPolicyRolloutPanel = React.lazy(() => import("../draggable-panels/PolicyRolloutPanel"));
const LazyCircleDetectionPanel = React.lazy(
  () => import("../draggable-panels/CircleDetectionPanel")
);
const LazyObjectLibraryPanel = React.lazy(() => import("../draggable-panels/ObjectLibraryPanel"));
const LazyMeshEditorPanel = React.lazy(() => import("../draggable-panels/MeshEditorPanel"));
const LazyGremlinsPanel = import.meta.env.DEV
  ? React.lazy(() => import("../draggable-panels/GremlinsPanel"))
  : null;
const MemoIkTargetPanel = React.memo(IkTargetPanel);
const MemoPhysicsPanel = React.memo(PhysicsPanel);
const MemoPhysicsRuntimePanel = React.memo(PhysicsRuntimePanel);
const MemoVisibilitySettingsPanel = React.memo(VisibilitySettingsPanel);
const MemoActuatorControlsPanel = React.memo(ActuatorControlsPanel);
const MemoBehaviorEditorPanel = React.memo(BehaviorEditorPanel);
const MemoOctoTrainingPanel = React.memo(OctoTrainingPanel);
const MemoCameraProbePanel = React.memo(CameraProbePanel);
const MemoSensorsPanel = React.memo(SensorsPanel);
const MemoLightsPanel = React.memo(LightsPanel);
const MemoHistoryPanel = React.memo(HistoryPanel);
const MemoXmlSourceOfTruthPanel = React.memo(XmlSourceOfTruthPanel);
const MemoTerrainPanel = React.memo(TerrainPanel);
const MemoAssetStatsPanel = React.memo(AssetStatsPanel);
const MemoSceneInventoryPanel = React.memo(SceneInventoryPanel);
const MemoModelCheckPanel = React.memo(ModelCheckPanel);
const MemoMassPropertiesPanel = React.memo(MassPropertiesPanel);
const MemoMeasurementsPanel = React.memo(MeasurementsPanel);
const MemoShortcutsPanel = React.memo(ShortcutsPanel);
const MemoAboutPanel = React.memo(AboutPanel);

function renderToolPanel({ name, title, titleIcon, open, onClose, mobilePinPanelId, children }) {
  return (
    <PanelErrorBoundary
      name={name}
      title={title}
      titleIcon={titleIcon}
      open={open}
      onClose={onClose}
      mobilePinPanelId={mobilePinPanelId}
      resetKeys={[open]}
    >
      {children}
    </PanelErrorBoundary>
  );
}

const LazyPanelFallback = React.memo(function LazyPanelFallback({
  open,
  onClose,
  title,
  titleIcon,
  mobilePinPanelId,
}) {
  return (
    <DraggablePanel
      open={open}
      onClose={onClose}
      title={title}
      titleIcon={titleIcon}
      mobilePinPanelId={mobilePinPanelId}
    >
      <div className="p-4 text-sm text-gray-300">Loading...</div>
    </DraggablePanel>
  );
});

const MainPanelToolPanels = React.memo(function MainPanelToolPanels({
  handleBodyReorder,
  handleOpenAssetLibraryFromSceneGraph,
  handleFiles,
  handlePoseEditorCreateKeyframe,
  handlePoseEditorDeleteKeyframe,
  motorGain,
  setMotorGain,
  showDevTools,
  showTrainingIkTools,
  openWarmstartDatasetPanel,
  openTrainingMetricsPanel,
  openPolicyRolloutPanel,
  openOctoTrainingPanel,
  policyRolloutRequest,
  loadingXmlSourceId,
  selectedXmlSourceId,
  handleXmlSourceSelect,
  aboutPanelMode,
}) {
  const {
    sceneGraphOpen,
    filePanelOpen,
    animationPanelOpen,
    materialEditorPanelOpen,
    lightsPanelOpen,
    ikTargetPanelOpen,
    runtimeControlsOpen,
    physicsPanelOpen,
    actuatorControlsOpen,
    warmstartDatasetOpen,
    behaviorEditorOpen,
    trainingPipelineOpen,
    octoTrainingOpen,
    trainingMetricsOpen,
    policyRolloutOpen,
    cameraProbeOpen,
    sensorsOpen,
    circleDetectionOpen,
    historyPanelOpen,
    shortcutsPanelOpen,
    getUpdatesPanelOpen,
    sendFeedbackPanelOpen,
    aboutPanelOpen,
    objectLibraryOpen,
    terrainPanelOpen,
    xmlSourcePanelOpen,
    runtimeDebugOpen,
    gremlinsPanelOpen,
    meshEditorOpen,
    visibilitySettingsOpen,
    sceneInventoryOpen,
    modelCheckOpen,
    massPropertiesOpen,
    measurementsOpen,
    actions,
  } = usePanelStore(
    useShallow((state) => ({
      sceneGraphOpen: state.sceneGraphOpen,
      filePanelOpen: state.filePanelOpen,
      animationPanelOpen: state.animationPanelOpen,
      materialEditorPanelOpen: state.materialEditorPanelOpen,
      lightsPanelOpen: state.lightsPanelOpen,
      ikTargetPanelOpen: state.ikTargetPanelOpen,
      runtimeControlsOpen: state.runtimeControlsOpen,
      physicsPanelOpen: state.physicsPanelOpen,
      actuatorControlsOpen: state.actuatorControlsOpen,
      warmstartDatasetOpen: state.warmstartDatasetOpen,
      behaviorEditorOpen: state.behaviorEditorOpen,
      trainingPipelineOpen: state.trainingPipelineOpen,
      octoTrainingOpen: state.octoTrainingOpen,
      trainingMetricsOpen: state.trainingMetricsOpen,
      policyRolloutOpen: state.policyRolloutOpen,
      cameraProbeOpen: state.cameraProbeOpen,
      sensorsOpen: state.sensorsOpen,
      circleDetectionOpen: state.circleDetectionOpen,
      historyPanelOpen: state.historyPanelOpen,
      shortcutsPanelOpen: state.shortcutsPanelOpen,
      getUpdatesPanelOpen: state.getUpdatesPanelOpen,
      sendFeedbackPanelOpen: state.sendFeedbackPanelOpen,
      aboutPanelOpen: state.aboutPanelOpen,
      objectLibraryOpen: state.objectLibraryPanelOpen,
      terrainPanelOpen: state.terrainPanelOpen,
      xmlSourcePanelOpen: state.xmlSourcePanelOpen,
      runtimeDebugOpen: state.runtimeDebugOpen,
      gremlinsPanelOpen: state.gremlinsPanelOpen,
      meshEditorOpen: state.meshEditorOpen,
      visibilitySettingsOpen: state.visibilitySettingsOpen,
      sceneInventoryOpen: state.sceneInventoryOpen,
      modelCheckOpen: state.modelCheckOpen,
      massPropertiesOpen: state.massPropertiesOpen,
      measurementsOpen: state.measurementsOpen,
      actions: state.actions,
    }))
  );
  const closeSceneGraph = React.useCallback(() => actions.setSceneGraphOpen(false), [actions]);
  const closeFilePanel = React.useCallback(() => actions.setFilePanelOpen(false), [actions]);
  const closeAnimationPanel = React.useCallback(
    () => actions.setAnimationPanelOpen(false),
    [actions]
  );
  const closeMaterialEditorPanel = React.useCallback(
    () => actions.setMaterialEditorPanelOpen(false),
    [actions]
  );
  const closeLightsPanel = React.useCallback(() => actions.setLightsPanelOpen(false), [actions]);
  const closeIkTargetPanel = React.useCallback(
    () => actions.setIkTargetPanelOpen(false),
    [actions]
  );
  const closePhysicsPanel = React.useCallback(() => actions.setPhysicsPanelOpen(false), [actions]);
  const closeRuntimeControlsPanel = React.useCallback(
    () => actions.setRuntimeControlsOpen(false),
    [actions]
  );
  const closeActuatorControlsPanel = React.useCallback(
    () => actions.setActuatorControlsOpen(false),
    [actions]
  );
  const closeWarmstartDatasetPanel = React.useCallback(
    () => actions.setWarmstartDatasetOpen(false),
    [actions]
  );
  const closeBehaviorEditorPanel = React.useCallback(
    () => actions.setBehaviorEditorOpen(false),
    [actions]
  );
  const closeTrainingPipelinePanel = React.useCallback(
    () => actions.setTrainingPipelineOpen(false),
    [actions]
  );
  const closeOctoTrainingPanel = React.useCallback(
    () => actions.setOctoTrainingOpen(false),
    [actions]
  );
  const closeTrainingMetricsPanel = React.useCallback(
    () => actions.setTrainingMetricsOpen(false),
    [actions]
  );
  const closePolicyRolloutPanel = React.useCallback(
    () => actions.setPolicyRolloutOpen(false),
    [actions]
  );
  const closeCameraProbePanel = React.useCallback(
    () => actions.setCameraProbeOpen(false),
    [actions]
  );
  const closeSensorsPanel = React.useCallback(() => actions.setSensorsOpen(false), [actions]);
  const closeCircleDetectionPanel = React.useCallback(
    () => actions.setCircleDetectionOpen(false),
    [actions]
  );
  const closeHistoryPanel = React.useCallback(() => actions.setHistoryPanelOpen(false), [actions]);
  const closeShortcutsPanel = React.useCallback(
    () => actions.setShortcutsPanelOpen(false),
    [actions]
  );
  const closeGetUpdatesPanel = React.useCallback(
    () => actions.setGetUpdatesPanelOpen(false),
    [actions]
  );
  const closeSendFeedbackPanel = React.useCallback(
    () => actions.setSendFeedbackPanelOpen(false),
    [actions]
  );
  const closeAboutPanel = React.useCallback(() => actions.setAboutPanelOpen(false), [actions]);
  const closeObjectLibraryPanel = React.useCallback(
    () => actions.setObjectLibraryPanelOpen(false),
    [actions]
  );
  const closeTerrainPanel = React.useCallback(() => actions.setTerrainPanelOpen(false), [actions]);
  const closeXmlSourcePanel = React.useCallback(
    () => actions.setXmlSourcePanelOpen(false),
    [actions]
  );
  const closeRuntimeDebugPanel = React.useCallback(
    () => actions.setRuntimeDebugOpen(false),
    [actions]
  );
  const closeGremlinsPanel = React.useCallback(
    () => actions.setGremlinsPanelOpen(false),
    [actions]
  );
  const closeMeshEditorPanel = React.useCallback(() => actions.setMeshEditorOpen(false), [actions]);
  const closeVisibilitySettings = React.useCallback(
    () => actions.setVisibilitySettingsOpen(false),
    [actions]
  );
  const closeSceneInventory = React.useCallback(
    () => actions.setSceneInventoryOpen(false),
    [actions]
  );
  const closeModelCheckPanel = React.useCallback(() => actions.setModelCheckOpen(false), [actions]);
  const closeMassPropertiesPanel = React.useCallback(
    () => actions.setMassPropertiesOpen(false),
    [actions]
  );
  const closeMeasurementsPanel = React.useCallback(
    () => actions.setMeasurementsOpen(false),
    [actions]
  );

  return (
    <>
      {sceneGraphOpen &&
        renderToolPanel({
          name: "scene-graph-panel",
          title: "Scene Graph",
          titleIcon: Network,
          open: sceneGraphOpen,
          onClose: closeSceneGraph,
          mobilePinPanelId: MOBILE_TOOL_PANEL_PIN_IDS.sceneGraph,
          children: (
            <MemoSceneGraphPanel
              open={sceneGraphOpen}
              onClose={closeSceneGraph}
              titleIcon={Network}
              onReorder={handleBodyReorder}
              onOpenAssetLibrary={handleOpenAssetLibraryFromSceneGraph}
              mobilePinPanelId={MOBILE_TOOL_PANEL_PIN_IDS.sceneGraph}
            />
          ),
        })}

      {filePanelOpen &&
        renderToolPanel({
          name: "file-upload-panel",
          title: "Upload Files",
          titleIcon: Upload,
          open: filePanelOpen,
          onClose: closeFilePanel,
          mobilePinPanelId: MOBILE_TOOL_PANEL_PIN_IDS.fileUpload,
          children: (
            <FileUploadPanelContainer
              open={filePanelOpen}
              onClose={closeFilePanel}
              titleIcon={Upload}
              handleFiles={handleFiles}
              mobilePinPanelId={MOBILE_TOOL_PANEL_PIN_IDS.fileUpload}
            />
          ),
        })}

      {animationPanelOpen &&
        renderToolPanel({
          name: "pose-editor-panel",
          title: "Pose Editor",
          titleIcon: Play,
          open: animationPanelOpen,
          onClose: closeAnimationPanel,
          mobilePinPanelId: MOBILE_TOOL_PANEL_PIN_IDS.poseEditor,
          children: (
            <PoseEditorPanelContainer
              open={animationPanelOpen}
              onClose={closeAnimationPanel}
              titleIcon={Play}
              onCreateKeyframe={handlePoseEditorCreateKeyframe}
              onDeleteKeyframe={handlePoseEditorDeleteKeyframe}
              mobilePinPanelId={MOBILE_TOOL_PANEL_PIN_IDS.poseEditor}
            />
          ),
        })}

      {materialEditorPanelOpen &&
        renderToolPanel({
          name: "material-editor-panel",
          title: "Material Editor",
          titleIcon: Palette,
          open: materialEditorPanelOpen,
          onClose: closeMaterialEditorPanel,
          mobilePinPanelId: MOBILE_TOOL_PANEL_PIN_IDS.materialEditor,
          children: (
            <React.Suspense
              fallback={
                <LazyPanelFallback
                  open={materialEditorPanelOpen}
                  onClose={closeMaterialEditorPanel}
                  title="Material Editor"
                  titleIcon={Palette}
                  mobilePinPanelId={MOBILE_TOOL_PANEL_PIN_IDS.materialEditor}
                />
              }
            >
              <LazyMaterialEditorPanel
                open={materialEditorPanelOpen}
                onClose={closeMaterialEditorPanel}
                titleIcon={Palette}
                mobilePinPanelId={MOBILE_TOOL_PANEL_PIN_IDS.materialEditor}
              />
            </React.Suspense>
          ),
        })}

      {lightsPanelOpen &&
        renderToolPanel({
          name: "lights-panel",
          title: "Lights",
          titleIcon: Lightbulb,
          open: lightsPanelOpen,
          onClose: closeLightsPanel,
          mobilePinPanelId: MOBILE_TOOL_PANEL_PIN_IDS.lights,
          children: (
            <MemoLightsPanel
              open={lightsPanelOpen}
              onClose={closeLightsPanel}
              titleIcon={Lightbulb}
              mobilePinPanelId={MOBILE_TOOL_PANEL_PIN_IDS.lights}
            />
          ),
        })}

      {ikTargetPanelOpen &&
        renderToolPanel({
          name: "ik-target-panel",
          title: "IK Targets",
          titleIcon: Link2,
          open: ikTargetPanelOpen,
          onClose: closeIkTargetPanel,
          mobilePinPanelId: MOBILE_TOOL_PANEL_PIN_IDS.ikTarget,
          children: (
            <MemoIkTargetPanel
              open={ikTargetPanelOpen}
              onClose={closeIkTargetPanel}
              titleIcon={Link2}
              mobilePinPanelId={MOBILE_TOOL_PANEL_PIN_IDS.ikTarget}
            />
          ),
        })}

      {physicsPanelOpen &&
        renderToolPanel({
          name: "physics-panel",
          title: "Physics Settings",
          titleIcon: Settings,
          open: physicsPanelOpen,
          onClose: closePhysicsPanel,
          mobilePinPanelId: MOBILE_TOOL_PANEL_PIN_IDS.physics,
          children: (
            <MemoPhysicsPanel
              open={physicsPanelOpen}
              onClose={closePhysicsPanel}
              titleIcon={Settings}
              mobilePinPanelId={MOBILE_TOOL_PANEL_PIN_IDS.physics}
            />
          ),
        })}
      {runtimeControlsOpen &&
        renderToolPanel({
          name: "runtime-controls-panel",
          title: "Runtime Controls & Stats",
          titleIcon: SlidersHorizontal,
          open: runtimeControlsOpen,
          onClose: closeRuntimeControlsPanel,
          mobilePinPanelId: MOBILE_TOOL_PANEL_PIN_IDS.runtimeControls,
          children: (
            <MemoPhysicsRuntimePanel
              open={runtimeControlsOpen}
              onClose={closeRuntimeControlsPanel}
              titleIcon={SlidersHorizontal}
              motorGain={motorGain}
              setMotorGain={setMotorGain}
              mobilePinPanelId={MOBILE_TOOL_PANEL_PIN_IDS.runtimeControls}
            />
          ),
        })}
      {actuatorControlsOpen &&
        renderToolPanel({
          name: "actuator-controls-panel",
          title: "Actuators",
          titleIcon: Zap,
          open: actuatorControlsOpen,
          onClose: closeActuatorControlsPanel,
          mobilePinPanelId: MOBILE_TOOL_PANEL_PIN_IDS.actuators,
          children: (
            <MemoActuatorControlsPanel
              open={actuatorControlsOpen}
              onClose={closeActuatorControlsPanel}
              titleIcon={Zap}
              motorGain={motorGain}
              mobilePinPanelId={MOBILE_TOOL_PANEL_PIN_IDS.actuators}
            />
          ),
        })}
      {showDevTools &&
        warmstartDatasetOpen &&
        renderToolPanel({
          name: "warmstart-dataset-panel",
          title: "Warm-start dataset (IK demos)",
          titleIcon: Package,
          open: warmstartDatasetOpen,
          onClose: closeWarmstartDatasetPanel,
          mobilePinPanelId: MOBILE_TOOL_PANEL_PIN_IDS.warmstart,
          children: (
            <React.Suspense
              fallback={
                <LazyPanelFallback
                  open={warmstartDatasetOpen}
                  onClose={closeWarmstartDatasetPanel}
                  title="Warm-start dataset (IK demos)"
                  titleIcon={Package}
                  mobilePinPanelId={MOBILE_TOOL_PANEL_PIN_IDS.warmstart}
                />
              }
            >
              <LazyWarmstartDatasetPanel
                open={warmstartDatasetOpen}
                onClose={closeWarmstartDatasetPanel}
                titleIcon={Package}
                mobilePinPanelId={MOBILE_TOOL_PANEL_PIN_IDS.warmstart}
              />
            </React.Suspense>
          ),
        })}
      {showTrainingIkTools &&
        behaviorEditorOpen &&
        renderToolPanel({
          name: "behavior-editor-panel",
          title: "Behavior Editor",
          titleIcon: GitBranch,
          open: behaviorEditorOpen,
          onClose: closeBehaviorEditorPanel,
          mobilePinPanelId: MOBILE_TOOL_PANEL_PIN_IDS.behaviorEditor,
          children: (
            <MemoBehaviorEditorPanel
              open={behaviorEditorOpen}
              onClose={closeBehaviorEditorPanel}
              titleIcon={GitBranch}
              mobilePinPanelId={MOBILE_TOOL_PANEL_PIN_IDS.behaviorEditor}
            />
          ),
        })}
      {showDevTools &&
        trainingPipelineOpen &&
        renderToolPanel({
          name: "training-pipeline-panel",
          title: "Training Setup",
          titleIcon: Play,
          open: trainingPipelineOpen,
          onClose: closeTrainingPipelinePanel,
          mobilePinPanelId: MOBILE_TOOL_PANEL_PIN_IDS.trainingPipeline,
          children: (
            <React.Suspense
              fallback={
                <LazyPanelFallback
                  open={trainingPipelineOpen}
                  onClose={closeTrainingPipelinePanel}
                  title="Training Setup"
                  titleIcon={Play}
                  mobilePinPanelId={MOBILE_TOOL_PANEL_PIN_IDS.trainingPipeline}
                />
              }
            >
              <LazyTrainingPipelinePanel
                open={trainingPipelineOpen}
                onClose={closeTrainingPipelinePanel}
                titleIcon={Play}
                onOpenWarmstart={openWarmstartDatasetPanel}
                onOpenMetrics={openTrainingMetricsPanel}
                onOpenRollout={openPolicyRolloutPanel}
                onOpenOctoTraining={openOctoTrainingPanel}
                mobilePinPanelId={MOBILE_TOOL_PANEL_PIN_IDS.trainingPipeline}
              />
            </React.Suspense>
          ),
        })}
      {showDevTools &&
        octoTrainingOpen &&
        renderToolPanel({
          name: "octo-training-panel",
          title: "Octo Training",
          titleIcon: Play,
          open: octoTrainingOpen,
          onClose: closeOctoTrainingPanel,
          mobilePinPanelId: MOBILE_TOOL_PANEL_PIN_IDS.octoTraining,
          children: (
            <MemoOctoTrainingPanel
              open={octoTrainingOpen}
              onClose={closeOctoTrainingPanel}
              titleIcon={Play}
              onOpenTrainingMetrics={openTrainingMetricsPanel}
              mobilePinPanelId={MOBILE_TOOL_PANEL_PIN_IDS.octoTraining}
            />
          ),
        })}
      {showDevTools &&
        trainingMetricsOpen &&
        renderToolPanel({
          name: "training-metrics-panel",
          title: "Training Metrics (Live)",
          titleIcon: BarChart3,
          open: trainingMetricsOpen,
          onClose: closeTrainingMetricsPanel,
          mobilePinPanelId: MOBILE_TOOL_PANEL_PIN_IDS.trainingMetrics,
          children: (
            <React.Suspense
              fallback={
                <LazyPanelFallback
                  open={trainingMetricsOpen}
                  onClose={closeTrainingMetricsPanel}
                  title="Training Metrics (Live)"
                  titleIcon={BarChart3}
                  mobilePinPanelId={MOBILE_TOOL_PANEL_PIN_IDS.trainingMetrics}
                />
              }
            >
              <LazyTrainingMetricsPanel
                open={trainingMetricsOpen}
                onClose={closeTrainingMetricsPanel}
                titleIcon={BarChart3}
                mobilePinPanelId={MOBILE_TOOL_PANEL_PIN_IDS.trainingMetrics}
              />
            </React.Suspense>
          ),
        })}
      {showDevTools &&
        policyRolloutOpen &&
        renderToolPanel({
          name: "policy-rollout-panel",
          title: "Policy rollout (stream)",
          titleIcon: Radio,
          open: policyRolloutOpen,
          onClose: closePolicyRolloutPanel,
          mobilePinPanelId: MOBILE_TOOL_PANEL_PIN_IDS.policyRollout,
          children: (
            <React.Suspense
              fallback={
                <LazyPanelFallback
                  open={policyRolloutOpen}
                  onClose={closePolicyRolloutPanel}
                  title="Policy rollout (stream)"
                  titleIcon={Radio}
                  mobilePinPanelId={MOBILE_TOOL_PANEL_PIN_IDS.policyRollout}
                />
              }
            >
              <LazyPolicyRolloutPanel
                open={policyRolloutOpen}
                onClose={closePolicyRolloutPanel}
                titleIcon={Radio}
                requestedPolicy={policyRolloutRequest}
                mobilePinPanelId={MOBILE_TOOL_PANEL_PIN_IDS.policyRollout}
              />
            </React.Suspense>
          ),
        })}
      {cameraProbeOpen &&
        renderToolPanel({
          name: "camera-probe-panel",
          title: "Camera Probe",
          titleIcon: Camera,
          open: cameraProbeOpen,
          onClose: closeCameraProbePanel,
          mobilePinPanelId: MOBILE_TOOL_PANEL_PIN_IDS.cameraProbe,
          children: (
            <MemoCameraProbePanel
              open={cameraProbeOpen}
              onClose={closeCameraProbePanel}
              titleIcon={Camera}
              mobilePinPanelId={MOBILE_TOOL_PANEL_PIN_IDS.cameraProbe}
            />
          ),
        })}
      {sensorsOpen &&
        renderToolPanel({
          name: "sensors-panel",
          title: "Sensors",
          titleIcon: Activity,
          open: sensorsOpen,
          onClose: closeSensorsPanel,
          mobilePinPanelId: MOBILE_TOOL_PANEL_PIN_IDS.sensors,
          children: (
            <MemoSensorsPanel
              open={sensorsOpen}
              onClose={closeSensorsPanel}
              titleIcon={Activity}
              mobilePinPanelId={MOBILE_TOOL_PANEL_PIN_IDS.sensors}
            />
          ),
        })}

      {circleDetectionOpen &&
        renderToolPanel({
          name: "circle-detection-panel",
          title: "Circle Detection",
          titleIcon: Grid,
          open: circleDetectionOpen,
          onClose: closeCircleDetectionPanel,
          mobilePinPanelId: MOBILE_TOOL_PANEL_PIN_IDS.circleDetection,
          children: (
            <React.Suspense
              fallback={
                <LazyPanelFallback
                  open={circleDetectionOpen}
                  onClose={closeCircleDetectionPanel}
                  title="Circle Detection"
                  titleIcon={Grid}
                  mobilePinPanelId={MOBILE_TOOL_PANEL_PIN_IDS.circleDetection}
                />
              }
            >
              <LazyCircleDetectionPanel
                open={circleDetectionOpen}
                onClose={closeCircleDetectionPanel}
                titleIcon={Grid}
                mobilePinPanelId={MOBILE_TOOL_PANEL_PIN_IDS.circleDetection}
              />
            </React.Suspense>
          ),
        })}
      {historyPanelOpen &&
        renderToolPanel({
          name: "history-panel",
          title: "History",
          titleIcon: History,
          open: historyPanelOpen,
          onClose: closeHistoryPanel,
          mobilePinPanelId: MOBILE_TOOL_PANEL_PIN_IDS.history,
          children: (
            <MemoHistoryPanel
              open={historyPanelOpen}
              onClose={closeHistoryPanel}
              titleIcon={History}
              mobilePinPanelId={MOBILE_TOOL_PANEL_PIN_IDS.history}
            />
          ),
        })}
      {shortcutsPanelOpen &&
        renderToolPanel({
          name: "shortcuts-panel",
          title: "Shortcuts",
          titleIcon: Keyboard,
          open: shortcutsPanelOpen,
          onClose: closeShortcutsPanel,
          children: (
            <MemoShortcutsPanel
              open={shortcutsPanelOpen}
              onClose={closeShortcutsPanel}
              titleIcon={Keyboard}
            />
          ),
        })}
      {getUpdatesPanelOpen &&
        renderToolPanel({
          name: "get-updates-panel",
          title: "Get Updates",
          titleIcon: Mail,
          open: getUpdatesPanelOpen,
          onClose: closeGetUpdatesPanel,
          children: (
            <React.Suspense fallback={null}>
              <LazyGetUpdatesPanel
                open={getUpdatesPanelOpen}
                onClose={closeGetUpdatesPanel}
                titleIcon={Mail}
              />
            </React.Suspense>
          ),
        })}
      {sendFeedbackPanelOpen &&
        renderToolPanel({
          name: "send-feedback-panel",
          title: "Send Feedback",
          titleIcon: MessageSquare,
          open: sendFeedbackPanelOpen,
          onClose: closeSendFeedbackPanel,
          children: (
            <React.Suspense fallback={null}>
              <LazySendFeedbackPanel
                open={sendFeedbackPanelOpen}
                onClose={closeSendFeedbackPanel}
                titleIcon={MessageSquare}
              />
            </React.Suspense>
          ),
        })}
      {aboutPanelOpen &&
        renderToolPanel({
          name: "about-panel",
          title: "About",
          titleIcon: Info,
          open: aboutPanelOpen,
          onClose: closeAboutPanel,
          children: (
            <MemoAboutPanel
              open={aboutPanelOpen}
              onClose={closeAboutPanel}
              titleIcon={Info}
              mode={aboutPanelMode}
            />
          ),
        })}
      {objectLibraryOpen &&
        renderToolPanel({
          name: "object-library-panel",
          title: "Library",
          titleIcon: SceneBuilderIcon,
          open: objectLibraryOpen,
          onClose: closeObjectLibraryPanel,
          mobilePinPanelId: MOBILE_TOOL_PANEL_PIN_IDS.objectLibrary,
          children: (
            <React.Suspense
              fallback={
                <LazyPanelFallback
                  open={objectLibraryOpen}
                  onClose={closeObjectLibraryPanel}
                  title="Library"
                  titleIcon={SceneBuilderIcon}
                  mobilePinPanelId={MOBILE_TOOL_PANEL_PIN_IDS.objectLibrary}
                />
              }
            >
              <LazyObjectLibraryPanel
                open={objectLibraryOpen}
                onClose={closeObjectLibraryPanel}
                titleIcon={SceneBuilderIcon}
                mobilePinPanelId={MOBILE_TOOL_PANEL_PIN_IDS.objectLibrary}
              />
            </React.Suspense>
          ),
        })}
      {terrainPanelOpen &&
        renderToolPanel({
          name: "terrain-panel",
          title: "Terrain",
          titleIcon: Mountain,
          open: terrainPanelOpen,
          onClose: closeTerrainPanel,
          mobilePinPanelId: MOBILE_TOOL_PANEL_PIN_IDS.terrain,
          children: (
            <MemoTerrainPanel
              open={terrainPanelOpen}
              onClose={closeTerrainPanel}
              titleIcon={Mountain}
              mobilePinPanelId={MOBILE_TOOL_PANEL_PIN_IDS.terrain}
            />
          ),
        })}
      {showDevTools &&
        xmlSourcePanelOpen &&
        renderToolPanel({
          name: "xml-source-panel",
          title: "XML Source",
          titleIcon: Upload,
          open: xmlSourcePanelOpen,
          onClose: closeXmlSourcePanel,
          mobilePinPanelId: MOBILE_TOOL_PANEL_PIN_IDS.xmlSource,
          children: (
            <MemoXmlSourceOfTruthPanel
              open={xmlSourcePanelOpen}
              onClose={closeXmlSourcePanel}
              titleIcon={Upload}
              groups={XML_SOURCE_GROUPS}
              loadingEntryId={loadingXmlSourceId}
              selectedEntryId={selectedXmlSourceId}
              onSelectEntry={handleXmlSourceSelect}
              mobilePinPanelId={MOBILE_TOOL_PANEL_PIN_IDS.xmlSource}
            />
          ),
        })}
      {runtimeDebugOpen &&
        renderToolPanel({
          name: "runtime-diagnostics-panel",
          title: "Runtime Diagnostics",
          titleIcon: BarChart3,
          open: runtimeDebugOpen,
          onClose: closeRuntimeDebugPanel,
          mobilePinPanelId: MOBILE_TOOL_PANEL_PIN_IDS.assetStats,
          children: (
            <MemoAssetStatsPanel
              open={runtimeDebugOpen}
              onClose={closeRuntimeDebugPanel}
              titleIcon={BarChart3}
              mobilePinPanelId={MOBILE_TOOL_PANEL_PIN_IDS.assetStats}
            />
          ),
        })}
      {import.meta.env.DEV &&
        LazyGremlinsPanel &&
        gremlinsPanelOpen &&
        renderToolPanel({
          name: "gremlins-panel",
          title: "Gremlins",
          titleIcon: Bug,
          open: gremlinsPanelOpen,
          onClose: closeGremlinsPanel,
          children: (
            <React.Suspense fallback={null}>
              <LazyGremlinsPanel
                open={gremlinsPanelOpen}
                onClose={closeGremlinsPanel}
                titleIcon={Bug}
              />
            </React.Suspense>
          ),
        })}
      {showDevTools &&
        meshEditorOpen &&
        renderToolPanel({
          name: "mesh-editor-panel",
          title: "Batch Mesh Origin Repair",
          titleIcon: Grid3X3,
          open: meshEditorOpen,
          onClose: closeMeshEditorPanel,
          mobilePinPanelId: MOBILE_TOOL_PANEL_PIN_IDS.meshEditor,
          children: (
            <React.Suspense
              fallback={
                <LazyPanelFallback
                  open={meshEditorOpen}
                  onClose={closeMeshEditorPanel}
                  title="Batch Mesh Origin Repair"
                  titleIcon={Grid3X3}
                  mobilePinPanelId={MOBILE_TOOL_PANEL_PIN_IDS.meshEditor}
                />
              }
            >
              <LazyMeshEditorPanel
                open={meshEditorOpen}
                onClose={closeMeshEditorPanel}
                titleIcon={Grid3X3}
                mobilePinPanelId={MOBILE_TOOL_PANEL_PIN_IDS.meshEditor}
              />
            </React.Suspense>
          ),
        })}
      {visibilitySettingsOpen &&
        renderToolPanel({
          name: "visibility-settings-panel",
          title: "Visibility Settings",
          titleIcon: SlidersHorizontal,
          open: visibilitySettingsOpen,
          onClose: closeVisibilitySettings,
          children: (
            <MemoVisibilitySettingsPanel
              open={visibilitySettingsOpen}
              onClose={closeVisibilitySettings}
              titleIcon={SlidersHorizontal}
            />
          ),
        })}
      {sceneInventoryOpen &&
        renderToolPanel({
          name: "scene-inventory-panel",
          title: "Scene Inventory",
          titleIcon: ListTree,
          open: sceneInventoryOpen,
          onClose: closeSceneInventory,
          mobilePinPanelId: MOBILE_TOOL_PANEL_PIN_IDS.sceneInventory,
          children: (
            <MemoSceneInventoryPanel
              open={sceneInventoryOpen}
              onClose={closeSceneInventory}
              titleIcon={ListTree}
              mobilePinPanelId={MOBILE_TOOL_PANEL_PIN_IDS.sceneInventory}
            />
          ),
        })}
      {modelCheckOpen &&
        renderToolPanel({
          name: "model-check-panel",
          title: "Check Model",
          titleIcon: ShieldCheck,
          open: modelCheckOpen,
          onClose: closeModelCheckPanel,
          mobilePinPanelId: MOBILE_TOOL_PANEL_PIN_IDS.modelCheck,
          children: (
            <MemoModelCheckPanel
              open={modelCheckOpen}
              onClose={closeModelCheckPanel}
              titleIcon={ShieldCheck}
              mobilePinPanelId={MOBILE_TOOL_PANEL_PIN_IDS.modelCheck}
            />
          ),
        })}
      {massPropertiesOpen &&
        renderToolPanel({
          name: "mass-properties-panel",
          title: "Mass & Balance",
          titleIcon: Weight,
          open: massPropertiesOpen,
          onClose: closeMassPropertiesPanel,
          mobilePinPanelId: MOBILE_TOOL_PANEL_PIN_IDS.massProperties,
          children: (
            <MemoMassPropertiesPanel
              open={massPropertiesOpen}
              onClose={closeMassPropertiesPanel}
              titleIcon={Weight}
              mobilePinPanelId={MOBILE_TOOL_PANEL_PIN_IDS.massProperties}
            />
          ),
        })}
      {showDevTools &&
        measurementsOpen &&
        renderToolPanel({
          name: "measurements-panel",
          title: "Measurements",
          titleIcon: Ruler,
          open: measurementsOpen,
          onClose: closeMeasurementsPanel,
          mobilePinPanelId: MOBILE_TOOL_PANEL_PIN_IDS.measurements,
          children: (
            <MemoMeasurementsPanel
              open={measurementsOpen}
              onClose={closeMeasurementsPanel}
              titleIcon={Ruler}
              mobilePinPanelId={MOBILE_TOOL_PANEL_PIN_IDS.measurements}
            />
          ),
        })}
    </>
  );
});

export default MainPanelToolPanels;
